// import { Link } from 'react-router-dom';
import {
  FaUserNurse,
  FaHandHoldingHeart,
  FaUserFriends,
  FaStethoscope,
  FaUserMd,
  FaVial,
  FaBed,
  FaAmbulance,
} from 'react-icons/fa';

const Services = () => {
  const services = [
    {
      id: 'home-nursing',
      title: 'Home Nursing',
      icon: <FaUserNurse className="text-4xl" />,
      description:
        'Professional nursing care including wound dressing, injections, medication management, and post-operative care.',
      features: ['Wound Care', 'Injections', 'Medication Management', 'Vital Monitoring'],
      color: 'blue',
    },
    {
      id: 'elderly-care',
      title: 'Elderly Care',
      icon: <FaHandHoldingHeart className="text-4xl" />,
      description:
        'Compassionate care for senior citizens including daily assistance, companionship, and health monitoring.',
      features: ['Daily Assistance', 'Companionship', 'Health Monitoring', 'Medication Reminders'],
      color: 'green',
    },
    {
      id: 'patient-attendants',
      title: 'Patient Attendants',
      icon: <FaUserFriends className="text-4xl" />,
      description:
        'Trained attendants for 24/7 patient care, hospital duty, and recovery assistance.',
      features: ['24/7 Care', 'Hospital Duty', 'Recovery Assistance', 'Personal Care'],
      color: 'purple',
    },
    {
      id: 'physiotherapy',
      title: 'Physiotherapy at Home',
      icon: <FaStethoscope className="text-4xl" />,
      description:
        'Professional physiotherapy sessions for rehabilitation, pain management, and mobility improvement.',
      features: ['Pain Management', 'Rehabilitation', 'Mobility Exercises', 'Post-Surgery Care'],
      color: 'indigo',
    },
    {
      id: 'doctor-visit',
      title: 'Doctor Home Visit',
      icon: <FaUserMd className="text-4xl" />,
      description:
        'Qualified doctors available for home consultations and medical checkups.',
      features: ['General Physician', 'Geriatric Care', 'Follow-up Visits', 'Medical Consultations'],
      color: 'red',
    },
    {
      id: 'lab-collection',
      title: 'Lab Sample Collection',
      icon: <FaVial className="text-4xl" />,
      description:
        'Home collection of blood samples and other lab tests with accurate results.',
      features: ['Blood Tests', 'Urine Tests', 'Sugar Monitoring', 'Report Delivery'],
      color: 'yellow',
    },
    {
      id: 'equipment-rental',
      title: 'Medical Equipment Rental',
      icon: <FaBed className="text-4xl" />,
      description:
        'Rental of medical equipment including wheelchairs, oxygen concentrators, and hospital beds.',
      features: ['Wheelchairs', 'Oxygen Concentrators', 'Hospital Beds', 'Nebulizers'],
      color: 'gray',
    },
    {
      id: 'ambulance',
      title: 'Ambulance Support',
      icon: <FaAmbulance className="text-4xl" />,
      description:
        '24/7 ambulance services with basic and advanced life support equipment.',
      features: ['24/7 Service', 'AC Ambulance', 'Medical Staff', 'Emergency Transfer'],
      color: 'orange',
    },
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      blue: 'bg-blue-50 border-blue-200 hover:bg-blue-100',
      green: 'bg-green-50 border-green-200 hover:bg-green-100',
      purple: 'bg-purple-50 border-purple-200 hover:bg-purple-100',
      indigo: 'bg-indigo-50 border-indigo-200 hover:bg-indigo-100',
      red: 'bg-red-50 border-red-200 hover:bg-red-100',
      yellow: 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100',
      gray: 'bg-gray-50 border-gray-200 hover:bg-gray-100',
      orange: 'bg-orange-50 border-orange-200 hover:bg-orange-100',
    };
    return colorMap[color] || 'bg-gray-50';
  };

  return (
    <div className="py-12">
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 font-poppins">
            Our Home Care Services
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive medical and home care services designed to provide comfort, 
            convenience, and professional Home Care at your doorstep in Haridwar.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => (
            <div
              key={service.id}
              className={`${getColorClasses(service.color)} border rounded-2xl p-8 transition-all duration-300 hover:shadow-xl`}
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className="p-4 rounded-xl bg-white shadow-md">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-800">{service.title}</h3>
              </div>
              
              <p className="text-gray-600 mb-6">{service.description}</p>
              
              <div className="mb-8">
                <h4 className="font-semibold text-gray-700 mb-3">Key Features:</h4>
                <ul className="space-y-2">
                  {service.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-gray-600">
                      <span className="w-2 h-2 bg-health-blue rounded-full mr-3"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex justify-between items-center">
                {/* 🔁 Changed Link → a, same path */}
                <a
                  href={`/services/${service.id}`}
                  className="text-health-blue font-semibold hover:text-blue-700 flex items-center"
                >
                  Learn More →
                </a>
                <a
                  href={`https://wa.me/918266958207?text=Hello! I'm interested in ${service.title} service`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-500 text-white px-6 py-2 rounded-full hover:bg-green-600 transition-colors text-sm font-semibold"
                >
                  Book Now
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-20 text-center">
          <div className="bg-gradient-to-r from-health-blue to-health-green rounded-2xl p-12 text-white">
            <h2 className="text-3xl font-bold mb-6 font-poppins">
              Need Help Choosing a Service?
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Our Home Care coordinators are available 24/7 to help you select 
              the right service for your needs.
            </p>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
              <a
                href="tel:+918266958207"
                className="bg-white text-health-blue px-8 py-4 rounded-full font-bold hover:bg-blue-50 transition-colors"
              >
                📞 Call for Consultation: +91 8266958207
              </a>
              <a
                href="https://wa.me/918266958207"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-500 text-white px-8 py-4 rounded-full font-bold hover:bg-green-600 transition-colors"
              >
                💬 Chat on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Services;
